import { makeAutoObservable } from 'mobx';

/** Tabs inside the Groups & Students settings modal */
export type StudySettingsTab = 'groups' | 'students';

/** Study editor mode */
export type StudyEditorMode = 'list' | 'view' | 'edit' | 'create';

/**
 * Study UI state - panel visibility and interaction tracking.
 */
export class StudyUiState {
  /** Study panel visibility */
  studyPanelVisible = false;

  /** Bumped each time the panel closes — the header study icon pulses
   *  briefly so the eye follows where the studies went (desktop UX). */
  studyCloseNonce = 0;

  /** Groups & Students settings modal — store-held (not component state)
   *  so guided-tour step actions can open it. */
  studySettingsOpen = false;
  studySettingsTab: StudySettingsTab = 'groups';

  /** Which group's accordion is open in the Groups tab — store-held so
   *  the tour can open a freshly created group. */
  expandedGroupId: string | null = null;

  /** Current editor mode */
  editorMode: StudyEditorMode = 'list';

  /** ID of study being edited (when in edit mode) */
  editingStudyId: string | null = null;

  /** ID of group being edited */
  editingGroupId: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /** Show study panel */
  showStudyPanel(): void {
    this.studyPanelVisible = true;
  }

  /** Hide study panel */
  hideStudyPanel(): void {
    if (this.studyPanelVisible) this.studyCloseNonce++;
    this.studyPanelVisible = false;
    this.studySettingsOpen = false;
    // Reset editing state when closing
    this.editorMode = 'list';
    this.editingStudyId = null;
    this.editingGroupId = null;
  }

  /** Open the Groups & Students settings modal (panel too, if needed) */
  openStudySettings(tab?: StudySettingsTab): void {
    this.studyPanelVisible = true;
    if (tab) this.studySettingsTab = tab;
    this.studySettingsOpen = true;
  }

  /** Close the Groups & Students settings modal */
  closeStudySettings(): void {
    this.studySettingsOpen = false;
    this.editingGroupId = null;
  }

  /** Switch tab inside the settings modal */
  setStudySettingsTab(tab: StudySettingsTab): void {
    this.studySettingsTab = tab;
  }

  /** Toggle a group's accordion in the Groups tab */
  toggleGroupExpansion(id: string): void {
    this.expandedGroupId = this.expandedGroupId === id ? null : id;
  }

  /** Open a specific group's accordion */
  expandGroup(id: string): void {
    this.expandedGroupId = id;
  }

  /** Set editor mode */
  setEditorMode(mode: StudyEditorMode): void {
    this.editorMode = mode;
  }

  /** Start creating a new study */
  startCreateStudy(): void {
    this.editorMode = 'create';
    this.editingStudyId = null;
  }

  /** Start editing a study */
  startEditStudy(id: string): void {
    this.editorMode = 'edit';
    this.editingStudyId = id;
  }

  /** View a study */
  viewStudy(id: string): void {
    this.editorMode = 'view';
    this.editingStudyId = id;
  }

  /** Go back to list */
  backToList(): void {
    this.editorMode = 'list';
    this.editingStudyId = null;
    this.editingGroupId = null;
  }

  /** Start editing a group */
  startEditGroup(id: string): void {
    this.editingGroupId = id;
  }

  /** Stop editing group */
  stopEditGroup(): void {
    this.editingGroupId = null;
  }
}
